export function getCanonical(
  canonicalTail: string | undefined,
  slug: any
) {
  let baseUrl = '';

  if (canonicalTail && canonicalTail !== undefined) {
    baseUrl = process.env.NEXT_PUBLIC_BASE_URL + canonicalTail;
  } else if (slug && slug !== undefined && slug.length > 0) {
    const tail = slug.join('/');
    baseUrl = `${ process.env.NEXT_PUBLIC_BASE_URL }/${ tail }`;
  } else {
    baseUrl = `${ process.env.NEXT_PUBLIC_BASE_URL }`;
  }

  return baseUrl;
}

export function getOpenGraphProp(seo: any) {
  if (seo?.openGraphImage) {
    return  {
      openGraph: {
        images: [
          {
            url: seo?.openGraphImage.asset.url,
          },
        ],
      },
    };
  }
}

interface BreadcrumbItem {
  slug: string;
  pageTitle: string;
}

interface ParentPage {
  _id?: string;
  pageTitle?: string;
  slug?: {
    current: string;
  };
  parent?: ParentPage;
}

interface ContactDetails {
  name?: string;
  address?: string;
  zipCode?: string;
  city?: string;
  postBox?: string;
  phoneNumber?: string;
  email?: string;
}

interface Location {
  _id: string;
  pageTitle: string;
  slug: {
    current: string;
  };
  parentSlug: string;
  parent?: ParentPage;
  contactDetails?: Pick<ContactDetails, 'address' | 'zipCode' | 'city' | 'postBox'>;
}

/** One FAQ entry: question name + answer (plain text from portable text) */
export interface FaqItem {
  name: string;
  text: string;
}

/** Section from page sectionBox: accordionSection or resolved component with sections */
interface SectionBoxItem {
  _type?: string;
  accordionBlocks?: Array<{
    accordionItems?: Array<{
      title?: string;
      description?: { text?: PortableTextBlock[] };
    }>;
  }>;
  sections?: SectionBoxItem[];
}

function extractAccordionItemsFromSectionBox(sectionBox: SectionBoxItem[] | null | undefined): FaqItem[] {
  const items: FaqItem[] = [];
  if (!Array.isArray(sectionBox)) return items;

  function collect(sections: SectionBoxItem[]) {
    for (const section of sections) {
      if (section._type === 'accordionSection' && Array.isArray(section.accordionBlocks)) {
        for (const block of section.accordionBlocks) {
          for (const accordionItem of block.accordionItems ?? []) {
            const name = accordionItem.title?.trim();
            if (!name) continue;
            const text = portableTextToPlainText(accordionItem.description?.text);
            items.push({ name, text });
          }
        }
      }
      if (section._type === 'component' && Array.isArray(section.sections)) {
        collect(section.sections);
      }
    }
  }

  collect(sectionBox);
  return items;
}

interface StructuredDataProps {
  parent?: ParentPage;
  slug?: string;
  pageTitle: string;
  locations?: Location[];
  contactDetails?: ContactDetails;
  availableProducts?: string[];
  /** For type 'faq': sectionBox to extract accordion Q&A */
  sectionBox?: SectionBoxItem[];
}

type PageType = 'root' | 'overzicht' | 'gemeente' | 'locatie' | 'faq' | string;

/** Portable text block from Sanity richText (array of blocks with children[].text) */
interface PortableTextBlock {
  children?: Array< { text?: string } >;
}

function portableTextToPlainText(blocks: PortableTextBlock[] | null | undefined): string {
  if (!Array.isArray(blocks)) return '';
  return blocks
    .map((block) =>
      (block?.children ?? []).map((c) => c?.text ?? '').join('')
    )
    .filter(Boolean)
    .join('\n\n');
}

function flattenParentChain(parent: ParentPage | undefined): BreadcrumbItem[] {
  const items: BreadcrumbItem[] = [];

  let current = parent;
  while (current) {
    // Skip root "/" slugs and empty slugs
    if (current.slug?.current && current.slug.current !== '/' && current.pageTitle) {
      items.unshift({
        slug: current.slug.current,
        pageTitle: current.pageTitle,
      });
    }
    current = current.parent;
  }

  return items;
}

function buildFullPath(baseUrl: string, parent: ParentPage | undefined, currentSlug: string | undefined): string {
  // Remove trailing slash from baseUrl
  const cleanBaseUrl = baseUrl.replace(/\/+$/, '');

  const parentItems = flattenParentChain(parent);
  const pathParts = parentItems.map(item => item.slug).filter(Boolean);
  if (currentSlug) {
    pathParts.push(currentSlug);
  }
  return pathParts.length > 0 ? `${cleanBaseUrl}/${pathParts.join('/')}` : cleanBaseUrl;
}

function getBreadcrumbStructuredData(parent: ParentPage | undefined, currentSlug: string, currentPageTitle: string) {
  const baseUrl = process.env.NEXT_PUBLIC_BASE_URL || 'https://www.prokino.nl';

  const items = [
    {
      "@type": "ListItem",
      "position": 1,
      "name": "Home",
      "item": baseUrl
    }
  ];

  // Build breadcrumb chain from parent data
  const parentItems = flattenParentChain(parent);

  let currentPath = '';
  parentItems.forEach((item, index) => {
    currentPath += `/${item.slug}`;
    items.push({
      "@type": "ListItem",
      "position": index + 2,
      "name": item.pageTitle,
      "item": `${baseUrl}${currentPath}`
    });
  });

  // Add current page as last item
  currentPath += `/${currentSlug}`;
  items.push({
    "@type": "ListItem",
    "position": items.length + 1,
    "name": currentPageTitle,
    "item": `${baseUrl}${currentPath}`
  });

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items
  };
}

export function getStructuredData(pageType: PageType, isIndexable: boolean, props: StructuredDataProps): object[] {
  if (!isIndexable) {
    return [];
  }

  const baseUrl = process.env.NEXT_PUBLIC_BASE_URL || 'https://www.prokino.nl';
  const currentPath = buildFullPath(baseUrl, props.parent, props.slug);

  const structuredData: object[] = [];

  switch (pageType) {
    case 'root':
      structuredData.push({
        "@context": "https://schema.org",
        "@type": "ChildCare",
        "@id": `${baseUrl}/#organization`,
        "name": "Prokino Kinderopvang",
        "email": "info@prokino.nl",
        "url": `${baseUrl}`,
        "logo": `${baseUrl}/assets/logos/kinderopvang.svg`,
        "telephone": "+31-88-2330000",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Diemermere 13",
          "addressLocality": "Diemen",
          "postalCode": "1112TB",
          "addressCountry": "NL"
        },
      });
      break;
    case 'overzicht':
      // Extract unique gemeentes from locations data
      const gemeentesMap = new Map<string, { pageTitle: string; slug: string }>();
      props.locations?.forEach((location) => {
        const parent = location.parent;
        if (parent?.slug?.current && parent?.pageTitle) {
          gemeentesMap.set(parent.slug.current, {
            pageTitle: parent.pageTitle,
            slug: parent.slug.current,
          });
        }
      });

      const gemeentesParts = Array.from(gemeentesMap.values())
        .sort((a, b) => a.pageTitle.localeCompare(b.pageTitle))
        .map((gemeente) => {
          const gemeenteUrl = `${currentPath}/${gemeente.slug}`;
          return {
            "@type": "CollectionPage",
            "@id": `${gemeenteUrl}#collection`,
            "name": gemeente.pageTitle,
            "url": gemeenteUrl
          };
        });

      structuredData.push(
        {
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "@id": `${currentPath}#collection`,
          "name": props.pageTitle,
          "url": currentPath,
          "isPartOf": {
            "@type": "WebSite",
            "@id": `${baseUrl}#website`
          },
          "about": {
            "@type": "Country",
            "name": "Nederland"
          },
          ...(gemeentesParts.length > 0 && { "hasPart": gemeentesParts })
        }
      );
      break;
    case 'gemeente':
      // Build hasPart from locations in this gemeente
      const locationsParts = props.locations?.map((location) => {
        const locationUrl = `${currentPath}/${location.slug.current}`;
        const loc: Record<string, unknown> = {
          "@type": "ChildCare",
          "@id": `${locationUrl}#location`,
          "name": location.pageTitle,
          "url": locationUrl
        };
        const cd = location.contactDetails;
        if (cd?.address && cd?.zipCode && cd?.city) {
          loc.address = {
            "@type": "PostalAddress",
            "streetAddress": cd.address,
            "addressLocality": cd.city,
            "postalCode": cd.zipCode,
            ...(cd.postBox && { "postOfficeBoxNumber": cd.postBox }),
            "addressCountry": "NL"
          };
        }
        return loc;
      }) || [];

      structuredData.push(
        {
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "@id": `${currentPath}#collection`,
          "name": props.pageTitle,
          "url": currentPath,
          "isPartOf": {
            "@type": "WebSite",
            "@id": `${baseUrl}#website`
          },
          "about": {
            "@type": "City",
            "name": props.pageTitle
          },
          ...(locationsParts.length > 0 && { "mainEntity": locationsParts })
        }
      );
      break;
    case 'locatie': {
      const cd = props.contactDetails;
      const productToSlug: Record<string, string> = {
        'Kinderdagverblijf': 'kdv',
        'Peuteropvang': 'pov',
        'Buitenschoolse opvang': 'bso',
      };

      const offers = props.availableProducts?.map((product) => ({
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "@id": `${currentPath}#${productToSlug[product] || product.toLowerCase().replace(/\s+/g, '-')}`,
          "name": product
        }
      })) ?? [];

      const locationData: Record<string, unknown> = {
        "@context": "https://schema.org",
        "@type": "ChildCare",
        "@id": `${currentPath}#location`,
        "name": props.pageTitle,
        "url": currentPath,
        "parentOrganization": { "@id": `${baseUrl}/#organization` }
      };

      if (cd?.address && cd?.zipCode && cd?.city) {
        locationData.address = {
          "@type": "PostalAddress",
          "streetAddress": cd.address,
          "addressLocality": cd.city,
          "postalCode": cd.zipCode,
          ...(cd.postBox && { "postOfficeBoxNumber": cd.postBox }),
          "addressCountry": "NL"
        };
      }
      if (cd?.phoneNumber) locationData.telephone = cd.phoneNumber;
      if (cd?.email) locationData.email = cd.email;
      if (offers.length > 0) locationData.makesOffer = offers;

      structuredData.push(locationData);
      break;
    }
    case 'faq': {
      const faqItems = extractAccordionItemsFromSectionBox(props.sectionBox);
      if (faqItems.length > 0) {
        structuredData.push({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqItems.map((item) => ({
            "@type": "Question",
            "name": item.name,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": item.text,
            },
          })),
        });
      }
      break;
    }
  }

  if (props.slug && pageType !== 'root') {
    structuredData.push(getBreadcrumbStructuredData(props.parent, props.slug, props.pageTitle));
  }

  return structuredData;
}
